export const PENDING_KEY = "sl:magicLinkPending";

export type MagicLinkMode = "login" | "register";

export interface PendingPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  mode: MagicLinkMode;
}

export type SendCheckAction = "magic-link" | "register-password";
export type SendCheckFailure =
  | { ok: false; reason: "captcha" }
  | { ok: false; reason: "throttled"; retryAfterSec: number }
  | { ok: false; reason: "network" };
export type SendCheckResult = { ok: true } | SendCheckFailure;

export async function checkSendAllowed(
  email: string,
  token: string,
  action: SendCheckAction,
): Promise<SendCheckResult> {
  try {
    const res = await fetch("/api/auth/magic-link-check", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, token, action }),
    });
    const data = (await res.json().catch(() => null)) as {
      ok?: boolean;
      reason?: string;
      retryAfterSec?: number;
    } | null;
    if (res.ok && data?.ok) return { ok: true };
    if (res.status === 429 && data?.reason === "throttled") {
      return {
        ok: false,
        reason: "throttled",
        retryAfterSec: Math.max(1, Math.ceil(data.retryAfterSec ?? 60)),
      };
    }
    if (data?.reason === "captcha") return { ok: false, reason: "captcha" };
    return { ok: false, reason: "network" };
  } catch {
    return { ok: false, reason: "network" };
  }
}

export type RequestSendFailure =
  | { ok: false; reason: "captcha" }
  | { ok: false; reason: "throttled"; retryAfterSec: number }
  | { ok: false; reason: "send-failed" }
  | { ok: false; reason: "no-account" }
  | { ok: false; reason: "network" };
export type RequestSendResult = { ok: true } | RequestSendFailure;

async function postJson(url: string, body: unknown, init?: RequestInit): Promise<RequestSendResult> {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
      body: JSON.stringify(body),
      ...init,
    });
    const data = (await res.json().catch(() => null)) as {
      ok?: boolean;
      reason?: string;
      retryAfterSec?: number;
    } | null;
    if (res.ok && data?.ok) return { ok: true };
    if (res.status === 429 && data?.reason === "throttled") {
      return {
        ok: false,
        reason: "throttled",
        retryAfterSec: Math.max(1, Math.ceil(data.retryAfterSec ?? 60)),
      };
    }
    if (data?.reason === "captcha") return { ok: false, reason: "captcha" };
    if (data?.reason === "send-failed") return { ok: false, reason: "send-failed" };
    if (data?.reason === "no-account") return { ok: false, reason: "no-account" };
    return { ok: false, reason: "network" };
  } catch {
    return { ok: false, reason: "network" };
  }
}

export async function requestMagicLink(args: {
  email: string;
  token: string;
  mode: MagicLinkMode;
  firstName?: string;
  lastName?: string;
}): Promise<RequestSendResult> {
  const { lastName, ...postBody } = args;
  const result = await postJson("/api/auth/send-magic-link", postBody);
  if (result.ok && typeof window !== "undefined") {
    const payload: PendingPayload = {
      email: args.email,
      firstName: args.firstName,
      lastName,
      mode: args.mode,
    };
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  }
  return result;
}

export async function requestPasswordReset(args: {
  email: string;
  token: string;
}): Promise<RequestSendResult> {
  return postJson("/api/auth/send-password-reset", args);
}

export async function requestEmailVerification(
  idToken: string,
): Promise<RequestSendResult> {
  return postJson("/api/auth/send-verification", {}, {
    headers: { authorization: `Bearer ${idToken}` },
  });
}

export function readPending(): PendingPayload | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingPayload;
  } catch {
    return null;
  }
}

export function clearPending(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(PENDING_KEY);
}
